// src/components/sections/PageCTA.jsx
// Drop-in CTA section. Uses the standard HOME CTA design.
//
// Usage:
//   import PageCTA from '../components/sections/PageCTA.jsx';
//   <PageCTA page="Home" setCurrentPage={setCurrentPage} />
//
// Valid page keys:
//   "Home" | "VisionPhilosophy" | "Products" | "IntelligenceCorner" | "Career" | "Connect"

import React from 'react';
import { PAGE_CTA } from '../../data/pageCTA.js';

const styles = `
  .pcta-mesh {
    background-image:
      radial-gradient(at 20% 50%, rgba(251,191,36,0.08) 0px, transparent 50%),
      radial-gradient(at 80% 50%, rgba(139,92,246,0.08) 0px, transparent 50%),
      radial-gradient(at 50% 90%, rgba(6,182,212,0.05) 0px, transparent 50%);
  }
  .pcta-grid {
    background-image:
      linear-gradient(rgba(251,191,36,0.025) 1px, transparent 1px),
      linear-gradient(90deg, rgba(251,191,36,0.025) 1px, transparent 1px);
    background-size: 40px 40px;
  }
  .pcta-btn-primary {
    position: relative; overflow: hidden;
    transition: all 0.3s ease;
  }
  .pcta-btn-primary::after {
    content: '';
    position: absolute; inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent);
    transform: translateX(-100%);
    transition: transform 0.5s ease;
  }
  .pcta-btn-primary:hover::after  { transform: translateX(100%); }
  .pcta-btn-primary:hover         { transform: translateY(-2px); background-color: #d97706; box-shadow: 0 8px 24px rgba(217,119,6,0.35); }
  .pcta-btn-secondary:hover       { transform: translateY(-2px); background: rgba(124,58,237,0.1); }
  .pcta-btn-ghost:hover           { transform: translateY(-2px); border-color: rgba(100,116,139,1); color: rgb(226,232,240); }
`;

const PageCTA = ({ page, setCurrentPage }) => {
  const cta = PAGE_CTA[page];
  if (!cta) return null;

  const buttons = cta.buttons(setCurrentPage);

  return (
    <>
      <style>{styles}</style>
      <section className="relative text-center p-14 rounded-2xl border border-amber-800/30 overflow-hidden">
        <div className="pcta-mesh absolute inset-0 rounded-2xl pointer-events-none" />
        <div className="pcta-grid absolute inset-0 rounded-2xl pointer-events-none" />

        <div className="relative z-10">
          <p className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-3">
            {cta.label}
          </p>
          <h2 className="text-3xl font-bold text-amber-400 mb-4">
            {cta.heading}
          </h2>
          <p className="text-violet-300 mb-8 max-w-xl mx-auto text-sm leading-relaxed">
            {cta.description}
          </p>

          <div className="flex flex-wrap gap-4 justify-center">
            {buttons.map((btn, i) => {
              if (btn.variant === "primary") return (
                <button key={i} onClick={btn.onClick}
                  className="pcta-btn-primary inline-flex items-center gap-2 bg-amber-600 text-black px-8 py-3 rounded-lg font-bold text-sm"
                  style={{ boxShadow: "0 4px 20px rgba(217,119,6,0.3)" }}>
                  {btn.label}
                </button>
              );
              if (btn.variant === "secondary") return (
                <button key={i} onClick={btn.onClick}
                  className="pcta-btn-secondary border-2 border-violet-600 text-violet-400 px-8 py-3 rounded-lg font-bold transition-all text-sm">
                  {btn.label}
                </button>
              );
              if (btn.variant === "ghost") return (
                <button key={i} onClick={btn.onClick}
                  className="pcta-btn-ghost border border-slate-700/60 text-slate-400 px-8 py-3 rounded-lg font-semibold transition-all text-sm">
                  {btn.label}
                </button>
              );
              return null;
            })}
          </div>
        </div>
      </section>
    </>
  );
};

export default PageCTA;